import api from './authService'
import { ESTRUCTURA_LEGAL } from '../data/normatividad'
import { obtenerCodigoErrorApi } from './apiError'
import {
  CategoriaNavegacion,
  ItemNavegacion,
  TemaNormativo,
} from '../types/normatividad'

const MAPEO_CATEGORIAS_ESTRUCTURA: Record<string, string> = {
  fiscal: 'leyes',
  comercio_exterior: 'tratados',
  clasificacion: 'tarifa',
  seguridad: 'noms',
  puertos: 'manuales',
  otros: 'manuales',
}

function clonarEstructura() {
  return ESTRUCTURA_LEGAL.map((cat) => ({
    ...cat,
    grupos: cat.grupos.map((grupo) => ({
      ...grupo,
      items: grupo.items.map((item) => {
        const nuevoItem: ItemNavegacion = { ...item }
        if (item.subItems) {
          nuevoItem.subItems = item.subItems.map((sub) => ({ ...sub }))
        }
        return nuevoItem
      }),
    })),
  }))
}

export const normatividadService = {
  // Obtener todas las normas desde la base de datos
  getAllNormas: async (): Promise<TemaNormativo[]> => {
    const response = await api.get('/api/normatividad')
    return response.data || []
  },

  getNormaById: async (id: string): Promise<TemaNormativo | undefined> => {
    try {
      const response = await api.get(`/api/normatividad/${id}`)
      return response.data
    } catch (error) {
      if (obtenerCodigoErrorApi(error) === 'not_found') {
        return undefined
      }
      throw error
    }
  },

  saveNorma: async (norma: TemaNormativo): Promise<void> => {
    const existe = await normatividadService.getNormaById(norma.id)

    if (existe) {
      await api.put(`/api/normatividad/${norma.id}`, norma)
    } else {
      await api.post('/api/normatividad', norma)
    }

    window.dispatchEvent(new Event('normatividad_updated'))
  },

  deleteNorma: async (id: string): Promise<void> => {
    await api.delete(`/api/normatividad/${id}`)
    window.dispatchEvent(new Event('normatividad_updated'))
  },

  // Obtener estructura de navegación
  getEstructuraNavegacion: async (): Promise<CategoriaNavegacion[]> => {
    const todas = await normatividadService.getAllNormas()
    const mapTodas = new Map(todas.map((n) => [n.id, n]))
    const nuevaEstructura = clonarEstructura()
    const idsEnEstructura = new Set<string>()

    const actualizarYRegistrar = (items: ItemNavegacion[]) => {
      items.forEach((item) => {
        if (item.id_doc) {
          idsEnEstructura.add(item.id_doc)
          const norma = mapTodas.get(item.id_doc)
          if (norma) {
            item.titulo = norma.titulo
            item.etiqueta = norma.actualizado
          }
        }
        if (item.subItems) actualizarYRegistrar(item.subItems)
      })
    }

    nuevaEstructura.forEach((cat) => cat.grupos.forEach((g) => actualizarYRegistrar(g.items)))

    const huerfanas = todas.filter((n) => !idsEnEstructura.has(n.id))
    if (huerfanas.length === 0) return nuevaEstructura

    huerfanas.forEach((norma) => {
      const catId = MAPEO_CATEGORIAS_ESTRUCTURA[norma.categoria] || 'leyes'
      const categoria = nuevaEstructura.find((c) => c.id === catId)

      if (categoria) {
        let grupo = categoria.grupos.find((g) => g.titulo === 'Nuevas / Agregadas')
        if (!grupo) {
          grupo = { titulo: 'Nuevas / Agregadas', items: [] }
          categoria.grupos.push(grupo)
        }
        if (!grupo.items.some((i) => i.id_doc === norma.id)) {
          grupo.items.push({
            titulo: norma.titulo,
            tipo: 'DOC',
            id_doc: norma.id,
            etiqueta: norma.actualizado || 'Nueva',
          })
        }
      }
    })

    return nuevaEstructura
  }
}

export default normatividadService
